import * as React from "react"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

export interface RowAction {
  /** Unique key for the action — used as React key. */
  key: string
  /** Lucide / tabler icon component instance, e.g. <Edit className="h-4 w-4" />. */
  icon: React.ReactNode
  /** Tooltip label shown on hover. */
  label: string
  /** Click handler. The bar handles stopPropagation so row clicks don't fire. */
  onClick: (e: React.MouseEvent) => void
  /** Hide this action entirely (use for permission gates). */
  hidden?: boolean
  /** Disable the button while keeping it visible. */
  disabled?: boolean
  /** Render this action with destructive (red) styling — used for delete. */
  destructive?: boolean
  /** Visual emphasis variant. */
  variant?: "ghost" | "outline"
}

interface RowActionBarProps extends React.HTMLAttributes<HTMLDivElement> {
  actions: RowAction[]
  /** When true, the icons appear only on row hover. Default false. */
  showOnHover?: boolean
}

/**
 * RowActionBar — inline icon row used in table cells in place of the legacy
 * `⋯` dropdown menu. Each action gets a tooltip-on-hover, permission filtering
 * via `hidden`, and consistent compact sizing. Clicks are isolated from row
 * click handlers automatically.
 */
export function RowActionBar({
  actions,
  showOnHover,
  className,
  ...props
}: RowActionBarProps) {
  const visible = actions.filter((a) => !a.hidden)
  if (visible.length === 0) return null

  return (
    <TooltipProvider delayDuration={150}>
      <div
        data-cell-id="actions"
        className={cn(
          "inline-flex items-center justify-end gap-0.5",
          showOnHover && "opacity-0 group-hover/row:opacity-100 transition-opacity",
          className
        )}
        onClick={(e) => e.stopPropagation()}
        {...props}
      >
        {visible.map((action) => (
          <Tooltip key={action.key}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant={action.variant ?? "ghost"}
                size="icon"
                disabled={action.disabled}
                onClick={(e) => {
                  e.stopPropagation()
                  action.onClick(e)
                }}
                aria-label={action.label}
                className={cn(
                  "h-8 w-8 rounded-md",
                  action.destructive
                    ? "text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    : "text-muted-foreground hover:text-primary hover:bg-primary/10"
                )}
              >
                {action.icon}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={4} className="text-xs">
              {action.label}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  )
}

RowActionBar.displayName = "RowActionBar"
